"use client";

import type * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { BookingFormDateRange } from "@/components/booking/BookingFormDateRange";
import { BookingLocationSelect } from "@/components/booking/BookingLocationSelect";
import { BookingPickupReturnFields } from "@/components/booking/BookingPickupReturnFields";
import { BookingReturnLocationPicker } from "@/components/booking/BookingReturnLocationPicker";
import { Button } from "@/components/ui/button";
import {
  getBranchOperationHours,
  isTimeWithinOperationHours,
} from "@/lib/booking/branch-hours";
import { findBranchByValue } from "@/lib/booking/locations";
import type {
  BookingSearchFormValues,
  VehicleType,
} from "@/lib/booking/search-params";
import { isBookingSearchFormComplete } from "@/lib/booking/search-params";
import { useBookingLocations } from "@/lib/hooks/use-booking-locations";
import { cn } from "@/lib/utils";
import { LucidePlus, LucideSearch } from "lucide-react";

type VehicleTabConfig = {
  id: VehicleType;
  label: string;
  searchLabel: string;
  icon?: string;
};

const homeTabs: VehicleTabConfig[] = [
  { id: "car", label: "Car & Passenger Van", searchLabel: "Show Car & Van" },
  {
    id: "delivery",
    label: "Delivery Van & Truck",
    searchLabel: "Show Van & Truck",
  },
];

const drawerTabs: VehicleTabConfig[] = [
  {
    id: "car",
    label: "Car & Passenger Van",
    searchLabel: "Show Cars",
    icon: "bi-car-front",
  },
  {
    id: "delivery",
    label: "Delivery Van & Truck",
    searchLabel: "Show Trucks",
    icon: "bi-truck",
  },
];

const fieldLabelClassName =
  "mb-1 block h-5 text-sm font-medium leading-5 text-heading";

const defaultValues: BookingSearchFormValues = {
  vehicle: "car",
  pickupLocation: "",
  returnLocation: "",
  differentReturn: false,
  range: undefined,
  pickupTime: undefined,
  returnTime: undefined,
};

export type BookingSearchFormProps = {
  variant?: "home" | "drawer";
  initialValues?: BookingSearchFormValues;
  onSubmit: (values: BookingSearchFormValues) => void;
};

export const BookingSearchForm: React.FC<BookingSearchFormProps> = ({
  variant = "home",
  initialValues,
  onSubmit,
}) => {
  const tabs = variant === "drawer" ? drawerTabs : homeTabs;
  const [vehicle, setVehicle] = useState<VehicleType>(
    initialValues?.vehicle ?? defaultValues.vehicle
  );
  const [pickupLocation, setPickupLocation] = useState(
    initialValues?.pickupLocation ?? ""
  );
  const [returnLocation, setReturnLocation] = useState(
    initialValues?.returnLocation ?? ""
  );
  const [differentReturn, setDifferentReturn] = useState(
    initialValues?.differentReturn ?? false
  );
  const [range, setRange] = useState(initialValues?.range);
  const [pickupTime, setPickupTime] = useState(initialValues?.pickupTime);
  const [returnTime, setReturnTime] = useState(initialValues?.returnTime);
  const [returnPopoverOpen, setReturnPopoverOpen] = useState(false);
  const { branches } = useBookingLocations();

  const pickupHours = useMemo(() => {
    const branch = findBranchByValue(branches, pickupLocation);
    return branch ? getBranchOperationHours(branch) : null;
  }, [branches, pickupLocation]);

  const returnHours = useMemo(() => {
    const returnLocationId =
      differentReturn && returnLocation ? returnLocation : pickupLocation;
    const branch = findBranchByValue(branches, returnLocationId);
    return branch ? getBranchOperationHours(branch) : null;
  }, [branches, differentReturn, pickupLocation, returnLocation]);

  useEffect(() => {
    if (
      pickupTime !== undefined &&
      pickupHours &&
      !isTimeWithinOperationHours(pickupTime, pickupHours)
    ) {
      setPickupTime(undefined);
    }
  }, [pickupHours, pickupLocation]);

  useEffect(() => {
    if (
      returnTime !== undefined &&
      returnHours &&
      !isTimeWithinOperationHours(returnTime, returnHours)
    ) {
      setReturnTime(undefined);
    }
  }, [returnHours, differentReturn, pickupLocation, returnLocation]);

  useEffect(() => {
    if (!initialValues) {
      return;
    }
    setVehicle(initialValues.vehicle);
    setPickupLocation(initialValues.pickupLocation);
    setReturnLocation(initialValues.returnLocation);
    setDifferentReturn(initialValues.differentReturn);
    setRange(initialValues.range);
    setPickupTime(initialValues.pickupTime);
    setReturnTime(initialValues.returnTime);
    setReturnPopoverOpen(false);
  }, [initialValues]);

  const handleDifferentReturnClick = () => {
    setDifferentReturn(true);
    setReturnPopoverOpen(true);
  };

  const handleReturnAtPickup = () => {
    setDifferentReturn(false);
    setReturnLocation("");
    setReturnPopoverOpen(false);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit({
      vehicle,
      pickupLocation,
      returnLocation,
      differentReturn,
      range,
      pickupTime,
      returnTime,
    });
  };

  const activeTab = tabs.find((tab) => tab.id === vehicle);
  const searchLabel = activeTab?.searchLabel ?? "Search";
  const validationOptions = { pickupHours, returnHours };
  const canSubmit = isBookingSearchFormComplete(
    {
      vehicle,
      pickupLocation,
      returnLocation,
      differentReturn,
      range,
      pickupTime,
      returnTime,
    },
    validationOptions,
  );

  if (variant === "drawer") {
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex gap-2" role="tablist" aria-label="Vehicle type">
          {drawerTabs.map((tab) => {
            const isActive = vehicle === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setVehicle(tab.id)}
                className={cn(
                  "inline-flex cursor-pointer items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                )}
              >
                {tab.icon ? (
                  <i className={cn(tab.icon, "text-base")} aria-hidden="true" />
                ) : null}
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-4">
          <div className="min-w-0 flex-[1.35]">
            <label htmlFor="drawer-pickup-location" className={fieldLabelClassName}>
              {differentReturn ? "Pickup" : "Pickup & return"}
            </label>
            <BookingLocationSelect
              id="drawer-pickup-location"
              value={pickupLocation}
              onValueChange={setPickupLocation}
            />
            {/* <button
              type="button"
              className="mt-2 cursor-pointer text-sm text-gray-600 underline-offset-2 hover:underline"
            >
              Apply corporate rate
            </button> */}
          </div>

          <div className="shrink-0">
            {differentReturn ? (
              <>
                <label
                  htmlFor="drawer-return-location"
                  className={fieldLabelClassName}
                >
                  Return
                </label>
                <BookingReturnLocationPicker
                  id="drawer-return-location"
                  pickupLocation={pickupLocation}
                  value={returnLocation}
                  onValueChange={setReturnLocation}
                  open={returnPopoverOpen}
                  onOpenChange={setReturnPopoverOpen}
                  onReturnAtPickup={handleReturnAtPickup}
                />
              </>
            ) : (
              <>
                <span
                  className={cn(fieldLabelClassName, "invisible select-none")}
                  aria-hidden="true"
                >
                  &#8203;
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 whitespace-nowrap"
                  onClick={handleDifferentReturnClick}
                >
                  <LucidePlus size={24}/>
                  Different return location
                </Button>
              </>
            )}
          </div>

          <div className="min-w-0 flex-2">
            <BookingFormDateRange
              variant="drawer"
              range={range}
              onRangeChange={setRange}
              pickupTime={pickupTime}
              onPickupTimeChange={setPickupTime}
              returnTime={returnTime}
              onReturnTimeChange={setReturnTime}
              pickupOpeningHours={pickupHours}
              returnOpeningHours={returnHours}
            />
          </div>

          <div className="shrink-0">
            <span
              className={cn(fieldLabelClassName, "invisible select-none")}
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Button
              type="submit"
              size="xl"
              disabled={!canSubmit}
              // className="h-12 min-w-[140px] rounded-xl bg-red-500 px-6 text-base font-semibold text-white hover:bg-orange-600"
            >
              {searchLabel}
            </Button>
          </div>
        </div>
      </form>
    );
  }

  return (
    <>
      <div className="flex" role="tablist" aria-label="Vehicle type">
        {homeTabs.map((tab, index) => {
          const isActive = vehicle === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setVehicle(tab.id)}
              className={cn(
                "cursor-pointer py-2 px-6 transition-colors",
                index === 0 && "rounded-tl-3xl",
                index === homeTabs.length - 1 && "rounded-tr-3xl",
                isActive
                  ? "bg-white font-semibold text-foreground"
                  : "bg-gray-400 text-gray-50"
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <form
        role="tabpanel"
        onSubmit={handleSubmit}
        className={cn(
          "bg-white rounded-b-3xl max-w-340 rounded-tr-3xl pb-6 px-6 pt-3 grid grid-cols-[repeat(4,minmax(0,1fr))_180px] gap-4"
        )}
      >
        <BookingPickupReturnFields
          pickupLocation={pickupLocation}
          onPickupLocationChange={setPickupLocation}
          returnLocation={returnLocation}
          onReturnLocationChange={setReturnLocation}
          differentReturn={differentReturn}
          onDifferentReturnChange={setDifferentReturn}
        />
        <BookingFormDateRange
          range={range}
          onRangeChange={setRange}
          pickupTime={pickupTime}
          onPickupTimeChange={setPickupTime}
          returnTime={returnTime}
          onReturnTimeChange={setReturnTime}
          pickupOpeningHours={pickupHours}
          returnOpeningHours={returnHours}
        />
        <Button type="submit" size="xl" className="mt-6" disabled={!canSubmit}>
          {/* {searchLabel} */}
          <LucideSearch size={20} className="mr-1"/>
          Search
        </Button>
      </form>
    </>
  );
};
